import { z } from 'zod';

import { ExportKind } from '../export';
import { mediaSchema } from '../media';
import { productCreateSchema, productSchema } from '../product';
import { productSectionCreateSchema, productSectionSchema } from '../product-section';
import { mongoIdSchema } from '../shared';

export const productSortAndFilter = z.object({
  createdBy: z.array(z.string()).nullish(),
  search: z.string().nullish(),
  offset: z.coerce.number().nullish(),
  limit: z.coerce.number().nullish(),
  sortBy: z
    .enum([
      'createdAt',
      'updatedAt',
      'createdBy',
      'primaryKeyword',
      'title',
      'uniqueIdentifier',
      'productBrandName',
      'price',
      'language',
      'country',
    ] as const)
    .nullish(),
  sortByDir: z.enum(['ASC', 'DESC']).nullish(),
});
export type ProductSortAndFilter = z.infer<typeof productSortAndFilter>;

//////////////////////////
// Product get socket event

const productSocketRes = z.object({
  product: productSchema,
  sections: z.array(productSectionSchema),
  medias: z.array(mediaSchema),
});
export const getProductSocketResSchema = z.object({
  list: z.array(productSocketRes),
  errors: z.array(
    z.object({
      productId: z.string(),
      errorMsg: z.string(),
      errorCode: z.number(),
    })
  ),
  listDeleted: z.array(z.string()),
});
export type GetProductSocketRes = z.infer<typeof getProductSocketResSchema>;

//////////////////////////
// List
export const listReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.preprocess(function (val) {
    if (val == null || typeof val !== 'object') {
      return val;
    }

    // NOTE: we want to make sure we convert the arrays on the query
    const obj = val as Record<string, unknown>;
    const createdBy = obj['createdBy[]'];
    if (createdBy != null) {
      obj['createdBy'] = Array.isArray(createdBy) ? createdBy : [createdBy];
    }

    return obj;
  }, productSortAndFilter.strip().nullish()),
  body: z.object({}).nullish(),
});
export type ListReq = z.infer<typeof listReqSchema>;

export const listResSchema = {
  200: z.object({
    totalCount: z.number(),
    data: z.object({
      products: z.array(productSchema),
      medias: z.array(mediaSchema),
    }),
  }),
};
export type ListRes = z.infer<(typeof listResSchema)[200]>;

//////////////////////////
// GetByBrandId
export const getProductsByBrandIdReqSchema = z.object({
  params: z.object({
    id: productSchema.shape.brandId,
  }),
  query: z.preprocess(function (val) {
    if (val == null || typeof val !== 'object') {
      return val;
    }

    // NOTE: we want to make sure we convert the arrays on the query
    const obj = val as Record<string, unknown>;
    const createdBy = obj['createdBy[]'];
    if (createdBy != null) {
      obj['createdBy'] = Array.isArray(createdBy) ? createdBy : [createdBy];
    }

    return obj;
  }, productSortAndFilter.strip().nullish()),
  body: z.object({}).nullish(),
});
export type GetProductsByBrandIdReq = z.infer<typeof getProductsByBrandIdReqSchema>;

export const getProductsByBrandIdResSchema = {
  200: z.object({
    totalCount: z.number(),
    data: z.object({
      products: z.array(productSchema),
      medias: z.array(mediaSchema),
    }),
  }),
};
export type GetProductsByBrandIdRes = z.infer<(typeof getProductsByBrandIdResSchema)[200]>;

//////////////////////////
// Get
export const getProductReqSchema = z.object({
  params: z.object({
    id: productSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetProductReq = z.infer<typeof getProductReqSchema>;

export const getProductResSchema = {
  200: z.object({
    data: z.object({
      product: productSchema,
      productSections: z.array(productSectionSchema),
    }),
  }),
};
export type GetProductRes = z.infer<(typeof getProductResSchema)[200]>;

//////////////////////////
// Create
export const createProductReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    product: productCreateSchema.strip(),
    sections: z.array(productSectionCreateSchema.strip()),
  }),
});
export type CreateProductReq = z.infer<typeof createProductReqSchema>;

export const createProductResSchema = {
  200: z.object({
    data: z.object({
      product: productSchema.strip(),
      sections: z.array(productSectionSchema.strip()),
    }),
  }),
};
export type CreateProductRes = z.infer<(typeof createProductResSchema)[200]>;

//////////////////////////
// Save
export const saveProductReqSchema = z.object({
  params: z.object({
    id: productSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    product: productCreateSchema.partial().strip().nullish(),
    sections: z.array(productSectionSchema.strip()).nullish(),
  }),
});
export type SaveProductReq = z.infer<typeof saveProductReqSchema>;

export const saveProductResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type SaveProductRes = z.infer<(typeof saveProductResSchema)[200]>;

//////////////////////////
// Save bulk
export const saveProductBulkReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    products: z.array(productSchema.partial().strip().nullish()),
    sections: z.array(productSectionSchema.strip()).nullish(),
  }),
});
export type SaveProductBulkReq = z.infer<typeof saveProductBulkReqSchema>;

export const saveProductBulkResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type SaveProductBulkRes = z.infer<(typeof saveProductBulkResSchema)[200]>;

//////////////////////////
// Retry generation
export const retryGenerationReqSchema = z.object({
  params: z.object({
    id: productSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RetryGenerationReq = z.infer<typeof retryGenerationReqSchema>;

export const retryGenerationResSchema = {
  200: z.object({
    data: z.object({
      product: productSchema.strip(),
    }),
  }),
};
export type RetryGenerationRes = z.infer<(typeof retryGenerationResSchema)[200]>;

//////////////////////////
// Delete
export const delProductReqSchema = z.object({
  params: z.object({
    id: productSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type DelProductReq = z.infer<typeof delProductReqSchema>;

export const delProductResSchema = {
  200: z.object({}),
};
export type DelProductRes = z.infer<(typeof delProductResSchema)[200]>;

//////////////////////////
// Delete bulk
export const delProductBulkReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    ids: z.array(productSchema.shape._id),
  }),
});
export type DelProductBulkReq = z.infer<typeof delProductBulkReqSchema>;

export const delProductBulkResSchema = {
  200: z.object({}),
};
export type DelProductBulkRes = z.infer<(typeof delProductBulkResSchema)[200]>;

//////////////////////////
// GenerateBulk
const fileSchema = z
  .object({
    name: z.string(),
    type: z.string(),
    size: z.number(),
    path: z.string(),
  })
  .strip();
export const generateBulkProductReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    organizationId: mongoIdSchema,
  }),
  files: z.object({
    file: z.union([fileSchema, z.array(fileSchema)]),
  }),
});
export type GenerateBulkProductReq = z.infer<typeof generateBulkProductReqSchema>;

export const generateBulkProductResSchema = {
  200: z.object({
    data: z.object({
      productIds: z.array(mongoIdSchema),
    }),
  }),
};
export type GenerateBulkProductRes = z.infer<(typeof generateBulkProductResSchema)[200]>;

export const exportProductReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    kind: z.nativeEnum(ExportKind).nullish(),
    ids: z.array(productSchema.shape._id),
  }),
});
export type ExportContentReq = z.infer<typeof exportProductReqSchema>;

export const exportProductResSchema = {
  200: z.object({
    data: z.string(),
  }),
};
export type ExportContentRes = z.infer<(typeof exportProductResSchema)[200]>;
